import React, { useState } from 'react';
import './AddHoroscopeForm.css';

const AddHoroscopeForm = ({ onClose, onSave }) => {
  const [formData, setFormData] = useState({
    sign: '',
    prediction: '',
    luckyNumbers: '',
    luckyColors: '',
    timespan: 'today',
    date: new Date().toISOString().split('T')[0]
  });

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const zodiacSigns = [
    { value: 'aries', label: '♈ Aries', emoji: '♈' },
    { value: 'taurus', label: '♉ Taurus', emoji: '♉' },
    { value: 'gemini', label: '♊ Gemini', emoji: '♊' },
    { value: 'cancer', label: '♋ Cancer', emoji: '♋' },
    { value: 'leo', label: '♌ Leo', emoji: '♌' },
    { value: 'virgo', label: '♍ Virgo', emoji: '♍' },
    { value: 'libra', label: '♎ Libra', emoji: '♎' },
    { value: 'scorpio', label: '♏ Scorpio', emoji: '♏' },
    { value: 'sagittarius', label: '♐ Sagittarius', emoji: '♐' },
    { value: 'capricorn', label: '♑ Capricorn', emoji: '♑' },
    { value: 'aquarius', label: '♒ Aquarius', emoji: '♒' },
    { value: 'pisces', label: '♓ Pisces', emoji: '♓' }
  ];

  const timespanOptions = [
    { value: 'today', label: '📅 Today', icon: '📅' },
    { value: 'tomorrow', label: '🔮 Tomorrow', icon: '🔮' },
    { value: 'weekly', label: '📆 Weekly', icon: '📆' },
    { value: 'monthly', label: '🗓️ Monthly', icon: '🗓️' },
    { value: 'yearly', label: '📊 Yearly', icon: '📊' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.sign) {
      setError('Please select a zodiac sign');
      return;
    }
    if (!formData.prediction.trim()) {
      setError('Please enter a prediction');
      return;
    }

    setLoading(true);
    try {
      await onSave(formData);
    } catch (error) {
      setError('Failed to save horoscope');
    } finally {
      setLoading(false);
    }
  };

  const selectedZodiac = zodiacSigns.find(z => z.value === formData.sign);

  return (
    <div className="add-horoscope-overlay">
      <div className="add-horoscope-form">
        <div className="form-header">
          <h2>🔮 Add New Horoscope</h2>
          <button onClick={onClose} className="close-button">✕</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h3>📍 Basic Information</h3>
            
            <div className="form-group">
              <label htmlFor="sign">Zodiac Sign *</label>
              <select
                id="sign"
                name="sign"
                value={formData.sign}
                onChange={handleChange}
                required
              >
                <option value="">Select Zodiac Sign</option>
                {zodiacSigns.map(sign => (
                  <option key={sign.value} value={sign.value}>
                    {sign.label}
                  </option>
                ))}
              </select>
              {selectedZodiac && (
                <div className="selected-zodiac">
                  <span className="zodiac-preview">{selectedZodiac.emoji}</span>
                  <span>{selectedZodiac.label}</span>
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="timespan">Time Period</label>
              <select
                id="timespan"
                name="timespan"
                value={formData.timespan}
                onChange={handleChange}
              >
                {timespanOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="date">Date</label>
              <input
                type="date"
                id="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-section">
            <h3>🔮 Prediction Content</h3>
            
            <div className="form-group">
              <label htmlFor="prediction">Horoscope Prediction *</label>
              <textarea
                id="prediction"
                name="prediction"
                value={formData.prediction}
                onChange={handleChange}
                placeholder="Enter the horoscope prediction for this zodiac sign..."
                rows={6}
                required
              />
              <div className="char-count">
                {formData.prediction.length} characters
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>🍀 Lucky Information</h3>
            
            <div className="form-group">
              <label htmlFor="luckyNumbers">Lucky Numbers</label>
              <input
                type="text"
                id="luckyNumbers"
                name="luckyNumbers"
                value={formData.luckyNumbers}
                onChange={handleChange}
                placeholder="e.g., 7, 14, 21, 35"
              />
              <small>Enter comma-separated numbers</small>
            </div>

            <div className="form-group">
              <label htmlFor="luckyColors">Lucky Colors</label>
              <input
                type="text"
                id="luckyColors"
                name="luckyColors"
                value={formData.luckyColors}
                onChange={handleChange}
                placeholder="e.g., Blue, Green, Golden"
              />
              <small>Enter comma-separated colors</small>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              onClick={onClose}
              className="cancel-button"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="save-button"
              disabled={loading}
            >
              {loading ? 'Saving...' : '🔮 Create Horoscope'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddHoroscopeForm;